import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ShieldCheck, CreditCard, ArrowRightCircle, CheckCircle2 } from 'lucide-react';
import DomainFAQ from './DomainFAQ';

interface DomainTrustAndLogisticsSectionProps {
  domainName: string;
}

const steps = (domainName: string) => [
  {
    title: 'Secure Payment',
    icon: CreditCard,
    description:
      'Pay with a credit card, Cash App, Klarna, Affirm, etc. For larger deals, I can support a managed transaction on request.',
  },
  {
    title: 'Transfer Initiated',
    icon: ArrowRightCircle,
    description:
      'I guide you through transferring it to your registrar or host. It can be yours in less than 24hrs from now.',
  },
  {
    title: 'You Attain the Domain',
    icon: CheckCircle2,
    description:
      `You get control of ${domainName}. Then if you hire me, I connect it to your new site and email system for you.`,
  },
];

export default function DomainTrustAndLogisticsSection({
  domainName,
}: DomainTrustAndLogisticsSectionProps) {
  const reduceMotion = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const staggerVariants = {
    hidden: {},
    show: {
      transition: { staggerChildren: reduceMotion ? 0 : 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
  };

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 space-y-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center space-y-3"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Transfers are fast and easy
          </h2>
        </motion.div>

        {/* Steps */}
        <motion.div
          variants={staggerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
        >
          {steps(domainName).map((step, idx) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="h-full rounded-2xl border border-slate-200 bg-white shadow-sm p-5 flex flex-col gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* FAQ */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 md:p-6"
        >
          <DomainFAQ />
        </motion.div>

      </div>
    </section>
  );
}

